import React, { useEffect, useState, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { getDefects, createDefect, updateDefect, deleteDefect, getProject, getUsersList } from '../services/api';
import { useToast } from '../App';
import { FiAlertOctagon, FiPlus, FiEdit2, FiTrash2, FiChevronDown, FiChevronRight, FiLink } from 'react-icons/fi';
import Modal, { ConfirmModal } from '../components/Modal';

const SEVERITE_OPTIONS = ['Bloquant', 'Majeur', 'Mineur', 'Trivial'];
const PRIORITE_OPTIONS = ['Critique', 'Haute', 'Moyenne', 'Basse'];
const STATUT_OPTIONS = ['Ouvert', 'En cours', 'Resolu', 'Ferme', 'Rejete'];
const ENV_OPTIONS = ['DEV', 'RECETTE', 'PREPROD', 'PROD'];

const SEVERITE_COLORS = {
  Bloquant: { color: '#dc2626', bg: '#fef2f2' },
  Majeur: { color: '#ea580c', bg: '#fff7ed' },
  Mineur: { color: '#ca8a04', bg: '#fefce8' },
  Trivial: { color: '#6b7280', bg: '#f9fafb' },
};

const STATUT_COLORS = {
  Ouvert: { color: '#dc2626', bg: '#fef2f2' },
  'En cours': { color: '#2563eb', bg: '#eff6ff' },
  Resolu: { color: '#16a34a', bg: '#f0fdf4' },
  Ferme: { color: '#6b7280', bg: '#f9fafb' },
  Rejete: { color: '#9333ea', bg: '#faf5ff' },
};

function Badge({ value, colors }) {
  const c = colors[value] || { color: '#6b7280', bg: '#f9fafb' };
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', padding: '2px 8px',
      borderRadius: '4px', fontSize: '0.72rem', fontWeight: 600,
      background: c.bg, color: c.color,
    }}>{value}</span>
  );
}

export default function DefectsPage() {
  const { id: projectId } = useParams();
  const addToast = useToast();
  const [defects, setDefects] = useState([]);
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filterStatut, setFilterStatut] = useState('');
  const [filterSeverite, setFilterSeverite] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editDefect, setEditDefect] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  const [users, setUsers] = useState([]);

  const [form, setForm] = useState({
    titre: '', description: '', severite: 'Majeur', priorite: 'Moyenne',
    environment: 'DEV', steps_to_reproduce: '', test_case_id: null, assigned_to_id: null,
  });

  const loadDefects = useCallback(async () => {
    try {
      const params = {};
      if (filterStatut) params.statut = filterStatut;
      if (filterSeverite) params.severite = filterSeverite;
      const { data } = await getDefects(projectId, params);
      setDefects(data);
    } catch { }
    setLoading(false);
  }, [projectId, filterStatut, filterSeverite]);

  const loadProject = useCallback(async () => {
    try {
      const { data } = await getProject(projectId);
      setProject(data);
    } catch { }
  }, [projectId]);

  const loadUsers = useCallback(async () => {
    try {
      const { data } = await getUsersList();
      setUsers(data);
    } catch { }
  }, []);

  useEffect(() => { loadProject(); }, [loadProject]);
  useEffect(() => { loadUsers(); }, [loadUsers]);
  useEffect(() => { loadDefects(); }, [loadDefects]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editDefect) {
        await updateDefect(projectId, editDefect.id, form);
        addToast('success', 'Defaut mis a jour');
      } else {
        await createDefect(projectId, form);
        addToast('success', 'Defaut cree');
      }
      setShowModal(false);
      setEditDefect(null);
      loadDefects();
    } catch (err) {
      addToast('error', err.response?.data?.detail || 'Erreur');
    }
  };

  const handleEdit = (d) => {
    setEditDefect(d);
    setForm({
      titre: d.titre, description: d.description, severite: d.severite,
      priorite: d.priorite, environment: d.environment,
      steps_to_reproduce: d.steps_to_reproduce, test_case_id: d.test_case_id,
      assigned_to_id: d.assigned_to_id,
    });
    setShowModal(true);
  };

  const handleNew = () => {
    setEditDefect(null);
    setForm({
      titre: '', description: '', severite: 'Majeur', priorite: 'Moyenne',
      environment: 'DEV', steps_to_reproduce: '', test_case_id: null, assigned_to_id: null,
    });
    setShowModal(true);
  };

  const handleDelete = async (d) => {
    try {
      await deleteDefect(projectId, d.id);
      addToast('success', 'Defaut supprime');
      setDeleteConfirm(null);
      loadDefects();
    } catch (err) {
      addToast('error', err.response?.data?.detail || 'Erreur');
    }
  };

  const handleStatusChange = async (d, newStatut) => {
    try {
      await updateDefect(projectId, d.id, { statut: newStatut });
      loadDefects();
    } catch (err) {
      addToast('error', err.response?.data?.detail || 'Erreur');
    }
  };

  // Stats
  const stats = { Ouvert: 0, 'En cours': 0, Resolu: 0, Ferme: 0, Rejete: 0 };
  defects.forEach(d => { stats[d.statut] = (stats[d.statut] || 0) + 1; });
  const totalAll = defects.length;

  const allTCs = project?.requirements?.flatMap(r => r.test_cases) || [];

  const formatDate = (d) => {
    if (!d) return '-';
    return new Date(d).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  if (loading) return (
    <div className="loading-spinner">
      <output className="spinner-border"></output>
      <span>Chargement...</span>
    </div>
  );

  return (
    <div>
      <div className="page-header">
        <div>
          <h2><FiAlertOctagon className="me-2" style={{ color: 'var(--danger)' }} />Defauts & Anomalies</h2>
          <p className="text-muted mb-0" style={{ fontSize: '0.85rem' }}>{project?.name} — Suivi des anomalies</p>
        </div>
        <button className="btn btn-primary d-flex align-items-center gap-1" onClick={handleNew}>
          <FiPlus /> Nouveau defaut
        </button>
      </div>

      {/* Stats */}
      <div className="exec-stats-bar mb-3">
        <button type="button" className={`exec-stat-chip ${!filterStatut ? 'active' : ''}`} style={{ background: 'none', border: 'none', font: 'inherit', color: 'inherit', cursor: 'pointer', padding: 0, textAlign: 'left' }} onClick={() => setFilterStatut('')}>
          <span className="exec-stat-chip-count">{totalAll}</span> Total
        </button>
        {STATUT_OPTIONS.map(s => {
          const c = STATUT_COLORS[s];
          const isActive = filterStatut === s;
          return (
            <button type="button" key={s}
                 className={`exec-stat-chip ${isActive ? 'active' : ''}`}
                 style={isActive ? { borderColor: c.color, background: c.bg, color: c.color, font: 'inherit', cursor: 'pointer', padding: 0, textAlign: 'left' } : { background: 'none', border: 'none', font: 'inherit', color: 'inherit', cursor: 'pointer', padding: 0, textAlign: 'left' }}
                 onClick={() => setFilterStatut(isActive ? '' : s)}>
              <span className="exec-stat-chip-count">{stats[s] || 0}</span> {s}
            </button>
          );
        })}
      </div>
      {/* Filter severite */}
      <div className="d-flex gap-2 mb-3 flex-wrap align-items-center">
        <span style={{ fontSize: '0.8rem', color: 'var(--gray-500)' }}>Severite :</span>
        <button className={`btn btn-sm ${!filterSeverite ? 'btn-outline-primary' : 'btn-outline-secondary'}`}
                style={{ fontSize: '0.75rem' }} onClick={() => setFilterSeverite('')}>Toutes</button>
        {SEVERITE_OPTIONS.map(s => (
          <button key={s}
                  className={`btn btn-sm ${filterSeverite === s ? 'btn-outline-primary' : 'btn-outline-secondary'}`}
                  style={{ fontSize: '0.75rem' }}
                  onClick={() => setFilterSeverite(filterSeverite === s ? '' : s)}>{s}</button>
        ))}
      </div>

      {/* List */}
      {defects.length === 0 ? (
        <div className="empty-state">
          <div className="empty-state-icon"><FiAlertOctagon /></div>
          <p>Aucun defaut {filterStatut || filterSeverite ? 'avec ces filtres' : 'enregistre'}.</p>
        </div>
      ) : (
        <div className="defects-list">
          {defects.map(d => {
            const isExpanded = expandedId === d.id;
            return (
              <div key={d.id} className={`defect-card ${isExpanded ? 'defect-card-expanded' : ''}`}>
                <button type="button" className="defect-row" style={{ background: 'none', border: 'none', font: 'inherit', color: 'inherit', cursor: 'pointer', padding: 0, textAlign: 'left', width: '100%' }} onClick={() => setExpandedId(isExpanded ? null : d.id)}>
                  {isExpanded ? <FiChevronDown className="defect-chevron" /> : <FiChevronRight className="defect-chevron" />}
                  <span className="defect-ref">{d.reference}</span>
                  <Badge value={d.severite} colors={SEVERITE_COLORS} />
                  <span className="defect-titre">{d.titre}</span>
                  <div className="d-flex align-items-center gap-2 ms-auto">
                    {d.ct_id && (
                      <span className="defect-linked-tc" title={d.cas_test}>
                        <FiLink style={{ fontSize: '0.7rem' }} /> {d.ct_id}
                      </span>
                    )}
                    <Badge value={d.statut} colors={STATUT_COLORS} />
                  </div>
                </button>

                {isExpanded && (
                  <div className="defect-detail">
                    <div className="defect-detail-grid">
                      <div className="defect-field">
                        <span className="defect-field-label">Priorite</span>
                        <span>{d.priorite}</span>
                      </div>
                      <div className="defect-field">
                        <span className="defect-field-label">Environnement</span>
                        <span>{d.environment}</span>
                      </div>
                      <div className="defect-field">
                        <span className="defect-field-label">Rapporte par</span>
                        <span>{d.reported_by_username || '-'}</span>
                      </div>
                      <div className="defect-field">
                        <span className="defect-field-label">Assigne a</span>
                        <span>{d.assigned_to_username || 'Non assigne'}</span>
                      </div>
                      <div className="defect-field">
                        <span className="defect-field-label">Cree le</span>
                        <span>{formatDate(d.created_at)}</span>
                      </div>
                      {d.resolved_at && (
                        <div className="defect-field">
                          <span className="defect-field-label">Resolu le</span>
                          <span>{formatDate(d.resolved_at)}</span>
                        </div>
                      )}
                      {d.closed_at && (
                        <div className="defect-field">
                          <span className="defect-field-label">Ferme le</span>
                          <span>{formatDate(d.closed_at)}</span>
                        </div>
                      )}
                    </div>

                    {d.description && (
                      <div className="defect-section">
                        <div className="defect-section-label">Description</div>
                        <div className="defect-section-content">{d.description}</div>
                      </div>
                    )}
                    {d.steps_to_reproduce && (
                      <div className="defect-section">
                        <div className="defect-section-label">Etapes de reproduction</div>
                        <div className="defect-section-content" style={{ whiteSpace: 'pre-wrap' }}>{d.steps_to_reproduce}</div>
                      </div>
                    )}
                    {d.ct_id && (
                      <div className="defect-section">
                        <div className="defect-section-label">Cas de test lie</div>
                        <div className="defect-section-content">
                          <span className="defect-linked-tc"><FiLink /> {d.ct_id} — {d.cas_test}</span>
                        </div>
                      </div>
                    )}

                    {/* Actions */}
                    <div className="defect-actions">
                      <div className="defect-status-flow">
                        {d.statut === 'Ouvert' && (
                          <button className="btn btn-sm btn-outline-primary" onClick={() => handleStatusChange(d, 'En cours')}>
                            Prendre en charge
                          </button>
                        )}
                        {d.statut === 'En cours' && (
                          <button className="btn btn-sm btn-outline-success" onClick={() => handleStatusChange(d, 'Resolu')}>
                            Marquer resolu
                          </button>
                        )}
                        {d.statut === 'Resolu' && (
                          <>
                            <button className="btn btn-sm btn-outline-success" onClick={() => handleStatusChange(d, 'Ferme')}>
                              Fermer
                            </button>
                            <button className="btn btn-sm btn-outline-warning" onClick={() => handleStatusChange(d, 'Ouvert')}>
                              Rouvrir
                            </button>
                          </>
                        )}
                        {(d.statut === 'Ouvert' || d.statut === 'En cours') && (
                          <button className="btn btn-sm btn-outline-secondary" onClick={() => handleStatusChange(d, 'Rejete')}>
                            Rejeter
                          </button>
                        )}
                        {(d.statut === 'Ferme' || d.statut === 'Rejete') && (
                          <button className="btn btn-sm btn-outline-warning" onClick={() => handleStatusChange(d, 'Ouvert')}>
                            Rouvrir
                          </button>
                        )}
                      </div>
                      <div className="d-flex gap-1">
                        <button className="btn btn-sm btn-ghost" onClick={() => handleEdit(d)} title="Modifier">
                          <FiEdit2 />
                        </button>
                        <button className="btn btn-sm btn-ghost text-danger" onClick={() => setDeleteConfirm(d)} title="Supprimer">
                          <FiTrash2 />
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
      {/* Modal creation/edition */}
      <Modal
        show={showModal}
        onClose={() => { setShowModal(false); setEditDefect(null); }}
        title={editDefect ? `Modifier ${editDefect.reference}` : 'Nouveau defaut'}
        icon={<FiAlertOctagon />}
        size="lg"
        footer={
          <>
            <button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => setShowModal(false)}>Annuler</button>
            <button type="submit" form="defect-form" className="btn btn-primary btn-sm">
              {editDefect ? 'Mettre a jour' : 'Creer le defaut'}
            </button>
          </>
        }
      >
        <form id="defect-form" onSubmit={handleSubmit}>
          <div className="form-group mb-3">
            <label className="form-label" htmlFor="field-titre">Titre *</label>
            <input id="field-titre" className="form-control" value={form.titre} required maxLength={300}
                   onChange={e => setForm({ ...form, titre: e.target.value })} />
          </div>
          <div className="form-group mb-3">
            <label className="form-label" htmlFor="field-description">Description</label>
            <textarea id="field-description" className="form-control" rows={3} value={form.description}
                      onChange={e => setForm({ ...form, description: e.target.value })} />
          </div>
          <div className="row mb-3">
            <div className="col-6">
              <label className="form-label" htmlFor="field-severite">Severite</label>
              <select id="field-severite" className="form-select" value={form.severite}
                      onChange={e => setForm({ ...form, severite: e.target.value })}>
                {SEVERITE_OPTIONS.map(s => <option key={s}>{s}</option>)}
              </select>
            </div>
            <div className="col-6">
              <label className="form-label" htmlFor="field-priorite">Priorite</label>
              <select id="field-priorite" className="form-select" value={form.priorite}
                      onChange={e => setForm({ ...form, priorite: e.target.value })}>
                {PRIORITE_OPTIONS.map(s => <option key={s}>{s}</option>)}
              </select>
            </div>
          </div>
          <div className="row mb-3">
            <div className="col-6">
              <label className="form-label" htmlFor="field-environment">Environnement</label>
              <select id="field-environment" className="form-select" value={form.environment}
                      onChange={e => setForm({ ...form, environment: e.target.value })}>
                {ENV_OPTIONS.map(s => <option key={s}>{s}</option>)}
              </select>
            </div>
            <div className="col-6">
              <label className="form-label" htmlFor="field-assigned-to">Assigne a</label>
              <select id="field-assigned-to" className="form-select"
                      value={form.assigned_to_id || ''}
                      onChange={e => setForm({ ...form, assigned_to_id: e.target.value ? Number.parseInt(e.target.value) : null })}>
                <option value="">Non assigne</option>
                {users.map(u => (
                  <option key={u.id} value={u.id}>{u.full_name || u.username}</option>
                ))}
              </select>
            </div>
          </div>
          <div className="row mb-3">
            <div className="col-6">
              <label className="form-label" htmlFor="field-test-case">Cas de test lie</label>
              <select id="field-test-case" className="form-select"
                      value={form.test_case_id || ''}
                      onChange={e => setForm({ ...form, test_case_id: e.target.value ? Number.parseInt(e.target.value) : null })}>
                <option value="">Aucun</option>
                {allTCs.map(tc => (
                  <option key={tc.id} value={tc.id}>{tc.ct_id} — {tc.cas_test}</option>
                ))}
              </select>
            </div>
          </div>
          <div className="form-group mb-3">
            <label className="form-label" htmlFor="field-steps">Etapes de reproduction</label>
            <textarea id="field-steps" className="form-control" rows={4} value={form.steps_to_reproduce}
                      placeholder={"1. Ouvrir la page...\n2. Cliquer sur...\n3. Observer que..."}
                      onChange={e => setForm({ ...form, steps_to_reproduce: e.target.value })} />
          </div>
        </form>
      </Modal>

      {/* Modal confirmation suppression */}
      <ConfirmModal
        show={!!deleteConfirm}
        onClose={() => setDeleteConfirm(null)}
        onConfirm={() => handleDelete(deleteConfirm)}
        title={`Supprimer ${deleteConfirm?.reference} ?`}
        message={<>Cette action est irreversible. Le defaut <strong>{deleteConfirm?.titre}</strong> sera supprime definitivement.</>}
        confirmText="Supprimer"
        confirmVariant="danger"
      />
    </div>
  );
}
